import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSelector } from 'reselect'

import {
  AppBar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  Toolbar,
  Typography,
  useTheme
} from '@material-ui/core'

import { ArrowBack, ArrowForwardIos } from '@material-ui/icons'
import { makeStyles, withStyles } from '@material-ui/core/styles'

import { useLiterals } from '~/components/Literals'
import { formatPhoneNumberHeinekenApi, formatDate } from '~/utils'
import api from '~/api'

import { getWhatsAppUrl } from '~/utils'
import actions from '~/actions'
import {
  fecharFormWhats,
  iniciarConversarFormWhats,
  proximoFormWhats,
  voltarFormWhats
} from '~/track'
import CustomDrawer from '~/components/Drawer'

const CheckboxWhats = withStyles({
  root: {
    color: '#121212',
    '&$checked': {
      color: '#25D366'
    }
  },
  checked: {}
})((props) => <Checkbox color="default" {...props} />)

const useStyles = makeStyles((theme) => ({
  root: {
    width: theme.gofind.asideWidth
  },
  paper: {
    position: 'absolute',
    height: '100%'
  },
  drawerMobile: {
    width: '100%',
    overflow: 'hidden',
    flexShrink: 0
  },
  drawerDesktop: {
    width: theme.gofind.asideWidth,
    overflow: 'hidden',
    flexShrink: 0
  },
  textField: {
    margin: 16,
    '& .MuiOutlinedInput-input': {
      padding: 16
    }
  },
  textFieldError: {
    color: theme.palette.error.main,
    marginLeft: 16
  },
  checkbox: {
    padding: 16,
    alignItems: 'flex-start'
  },
  button: {
    textTransform: 'none',
    padding: '12px 40px',
    margin: 16,
    transition: 'all 0.2s ease-in-out',
    alignSelf: 'flex-end'
  },
  toolbar: {
    gap: 16,
    display: 'flex',
    alignItems: 'center',
    '& .MuiButtonBase-root': {
      padding: 8
    }
  }
}))

const initialForm = {
  email: '',
  name: '',
  lastName: '',
  phone: '',
  birth: ''
}

const initialChecks = { checkStorage: false, checkSendMail: false }

const selector = createSelector(
  (state) => state.config,
  (state) => state.app.currentProduct,
  (state) => state.app.currentStore,
  (config, currentProduct, currentStore) => ({ config, currentProduct, currentStore })
)

const ERRORS = Object.freeze({
  BIRTH_FORMAT: Symbol('BIRTH_FORMAT'),
  IS_UNDERAGE: Symbol('IS_UNDERAGE'),
  PHONE_FORMAT: Symbol('PHONE_FORMAT'),
  PHONE_EMPTY: Symbol('PHONE_EMPTY'),
  EMAIL_EMPTY: Symbol('EMAIL_EMPTY'),
  FIRST_NAME_EMPTY: Symbol('FIRST_NAME_EMPTY'),
  LAST_NAME_EMPTY: Symbol('LAST_NAME_EMPTY'),
  BIRTH_EMPTY: Symbol('BIRTH_EMPTY'),
  CHECK_STORAGE_FALSE: Symbol('CHECK_STORAGE_FALSE')
})

const isUnderage = (date) => {
  const [day, mounth, year] = date.split('/')

  const today = new Date()
  const birth = new Date(Number(year), Number(mounth) - 1, Number(day))

  const age = (today - birth) / 31557600000

  return age < 18
}

export default function WhatsappFormDialog({ open, setOpen }) {
  const dispatch = useDispatch()
  const { config, currentProduct, currentStore } = useSelector(selector)

  const classes = useStyles()
  const theme = useTheme()
  const [errors, setErrors] = useState([])
  const [sendingForm, setSendingForm] = useState(false)
  const [formWhats, setFormWhats] = useState(initialForm)
  const [formCheck, setFormCheck] = useState(initialChecks)
  const [nextStep, setNextStep] = useState(false)

  const { getLiteral } = useLiterals()

  const handleClose = () => {
    fecharFormWhats()
    setOpen(false)
    setNextStep(false)
    setErrors([])
  }

  const cleanPhone = (value) => {
    return String(value).replace(/\D/g, '')
  }

  const handleInputChange = (event) => {
    const { name, value } = event.target
    setErrors([])

    switch (name) {
      case 'phone':
        const cleanedPhone = cleanPhone(value)
        setFormWhats({ ...formWhats, phone: cleanedPhone })
        return
      case 'birth':
        if (value.length > 10) return
        let formatedBirth = formatDate(value)
        setFormWhats({ ...formWhats, birth: formatedBirth })
        return
    }

    setFormWhats({ ...formWhats, [name]: value })
  }

  const handleChange = (event) => {
    const { name, checked } = event.target

    setFormCheck({ ...formCheck, [name]: checked })
    setErrors([])
  }

  const handleSubmitForm = () => {
    const { email, name, lastName, phone, birth } = formWhats
    const { checkStorage, checkSendMail } = formCheck

    if (!checkStorage) {
      setErrors([...errors, ERRORS.CHECK_STORAGE_FALSE])
      return
    }

    if (errors.length || !currentProduct || !currentStore || !config.brandName) {
      return
    }

    setSendingForm(true)
    api
      .postWhatsappData({
        email,
        name,
        lastName,
        phone: formatPhoneNumberHeinekenApi(phone),
        birth,
        checkStorage,
        checkSendMail
      })
      .then((response) => {
        if (response?.message?.toLowerCase() === 'sucesso') {
          iniciarConversarFormWhats()
          dispatch(actions.snack.add({ id: Date.now(), message: getLiteral('1543') }))
        }
        return response
      })
      .then(() => {
        const { whatsapp, name: storeName } = currentStore
        const { name: productName } = currentProduct

        // open new tab on whatsapp web
        window.open(
          getWhatsAppUrl(
            whatsapp,
            storeName,
            productName,
            config.brandName,
            Boolean(config.showContextViewEstablishment),
            getLiteral
          )
        )
        setFormWhats(initialForm)
        setFormCheck(initialChecks)
        setNextStep(false)
        setOpen(false)
      })
      .catch((response) => {
        dispatch(actions.snack.add({ id: Date.now(), message: getLiteral('1808') }))
        console.error('Erro ao salvar whatsapp', response)
      })
      .finally(() => {
        setSendingForm(false)
      })
  }

  const handleNext = () => {
    const { email, name, lastName, phone, birth } = formWhats

    const errorsFounded = []

    !email && errorsFounded.push(ERRORS.EMAIL_EMPTY)
    !name && errorsFounded.push(ERRORS.FIRST_NAME_EMPTY)
    !lastName && errorsFounded.push(ERRORS.LAST_NAME_EMPTY)
    !phone && errorsFounded.push(ERRORS.PHONE_EMPTY)
    !birth && errorsFounded.push(ERRORS.BIRTH_EMPTY)

    // Verify is phone number stats and end with dd-ddddd-dddd
    if (!/^(\d{2}-\d{5}-\d{4})$/.test(formatPhoneNumberHeinekenApi(phone))) {
      errorsFounded.push(ERRORS.PHONE_FORMAT)
    }

    if (!/^(\d{2}\/\d{2}\/\d{4})$/.test(birth)) {
      errorsFounded.push(ERRORS.BIRTH_FORMAT)
    }

    if (isUnderage(birth)) {
      errorsFounded.push(ERRORS.IS_UNDERAGE)
    }

    setErrors(errorsFounded)

    if (errorsFounded.length === 0) {
      proximoFormWhats()
      setNextStep(true)
    }
  }

  return (
    <CustomDrawer id="drawer-whatsapp-form" open={open}>
      <AppBar position="static">
        <Toolbar classes={{ root: classes.toolbar }}>
          <IconButton edge="start" color="inherit" onClick={handleClose}>
            <ArrowBack />
          </IconButton>
          <Typography>{getLiteral('2473')}</Typography>
        </Toolbar>
      </AppBar>

      <Box style={{ width: '100%', padding: '22px 16px' }}>
        {<Typography>{getLiteral('2477')}</Typography>}
      </Box>

      {nextStep ? (
        <>
          <FormControlLabel
            className={classes.checkbox}
            color={
              errors && !formCheck.checkStorage
                ? theme.palette.error.main
                : theme.palette.success.main
            }
            control={
              <CheckboxWhats
                checked={formCheck.checkStorage}
                onChange={handleChange}
                name="checkStorage"
                style={
                  errors.includes(ERRORS.CHECK_STORAGE_FALSE)
                    ? { color: theme.palette.error.main }
                    : { color: 'inherit ' }
                }
              />
            }
            label={getLiteral('2478')}
          />
          {errors.includes(ERRORS.CHECK_STORAGE_FALSE) && (
            <span className={classes.textFieldError}>{getLiteral('2494')}</span>
          )}

          <FormControlLabel
            className={classes.checkbox}
            control={
              <CheckboxWhats
                checked={formCheck.checkSendMail}
                onChange={handleChange}
                name="checkSendMail"
                style={{ color: 'inherit' }}
              />
            }
            label={getLiteral('2479')}
          />
        </>
      ) : (
        <>
          <TextField
            label={getLiteral('1067')}
            placeholder={`${getLiteral('1067')}*`}
            variant="outlined"
            name="name"
            value={formWhats.name}
            onChange={handleInputChange}
            className={classes.textField}
            style={{ marginBottom: errors.includes(ERRORS.FIRST_NAME_EMPTY) ? 5 : 16 }}
            error={errors.includes(ERRORS.FIRST_NAME_EMPTY)}
            InputLabelProps={{ shrink: true }}
          />
          {errors.includes(ERRORS.FIRST_NAME_EMPTY) && (
            <span className={classes.textFieldError}>{getLiteral('1346')}</span>
          )}

          <TextField
            label={getLiteral('2480')}
            placeholder={`${getLiteral('2480')}*`}
            variant="outlined"
            name="lastName"
            value={formWhats.lastName}
            onChange={handleInputChange}
            className={classes.textField}
            style={{ marginBottom: errors.includes(ERRORS.LAST_NAME_EMPTY) ? 5 : 16 }}
            error={errors.includes(ERRORS.LAST_NAME_EMPTY)}
            InputLabelProps={{ shrink: true }}
          />
          {errors.includes(ERRORS.LAST_NAME_EMPTY) && (
            <span className={classes.textFieldError}>{getLiteral('1346')}</span>
          )}

          <TextField
            label="E-mail"
            placeholder="E-mail*"
            variant="outlined"
            name="email"
            value={formWhats.email}
            onChange={handleInputChange}
            className={classes.textField}
            style={{ marginBottom: errors.includes(ERRORS.EMAIL_EMPTY) ? 5 : 16 }}
            error={errors.includes(ERRORS.EMAIL_EMPTY)}
            InputLabelProps={{ shrink: true }}
          />
          {errors.includes(ERRORS.EMAIL_EMPTY) && (
            <span className={classes.textFieldError}>
              {`${getLiteral('1346')} | ${getLiteral('1697')}: john.doe@example.com`}
            </span>
          )}

          <TextField
            label={getLiteral('2481')}
            placeholder={`${getLiteral('2481')}*`}
            variant="outlined"
            name="birth"
            value={formWhats.birth}
            onChange={handleInputChange}
            className={classes.textField}
            style={{
              marginBottom: errors.some((err) =>
                [ERRORS.BIRTH_EMPTY, ERRORS.BIRTH_EMPTY, ERRORS.IS_UNDERAGE].includes(err)
              )
                ? 5
                : 16
            }}
            maxLength="10"
            error={errors.some((err) =>
              [ERRORS.BIRTH_EMPTY, ERRORS.BIRTH_EMPTY, ERRORS.IS_UNDERAGE].includes(err)
            )}
            InputLabelProps={{ shrink: true }}
          />
          {errors.some((err) => [ERRORS.BIRTH_EMPTY, ERRORS.BIRTH_FORMAT].includes(err)) && (
            <span className={classes.textFieldError}>
              {`${getLiteral('1346')} | ${getLiteral('1697')}: dd/mm/yyyy`}
            </span>
          )}
          {errors.includes(ERRORS.IS_UNDERAGE) && (
            <span className={classes.textFieldError}>{getLiteral('2499')}</span>
          )}

          <TextField
            label={getLiteral('2482')}
            placeholder={getLiteral('2482')}
            variant="outlined"
            name="phone"
            value={formatPhoneNumberHeinekenApi(formWhats.phone)}
            onChange={(event) => {
              const valueSize = cleanPhone(event.target.value).length
              if (valueSize > 11) return
              handleInputChange(event)
            }}
            className={classes.textField}
            style={{
              marginBottom: errors.some((err) =>
                [ERRORS.PHONE_EMPTY, ERRORS.PHONE_FORMAT].includes(err)
              )
                ? 5
                : 16
            }}
            error={errors.some((err) => [ERRORS.PHONE_EMPTY, ERRORS.PHONE_FORMAT].includes(err))}
            InputLabelProps={{ shrink: true }}
          />
          {errors.includes(ERRORS.PHONE_EMPTY) && (
            <span className={classes.textFieldError}>{getLiteral('1346')}</span>
          )}
          {errors.includes(ERRORS.PHONE_FORMAT) && (
            <span className={classes.textFieldError}>{getLiteral('2483')}</span>
          )}
        </>
      )}

      {nextStep ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
          <Button
            onClick={() => {
              voltarFormWhats()
              setNextStep(false)
            }}
            variant="outlined"
            style={{ textTransform: 'none' }}
          >
            {getLiteral('1051')}
          </Button>
          <Button
            onClick={handleSubmitForm}
            className={classes.button}
            disabled={sendingForm}
            variant="contained"
            color="primary"
          >
            {sendingForm ? `${getLiteral('1745')}...` : getLiteral('2484')}
          </Button>
        </div>
      ) : (
        <Button
          onClick={handleNext}
          endIcon={<ArrowForwardIos style={{ fontSize: 16 }} />}
          className={classes.button}
          variant="contained"
          color="primary"
        >
          {getLiteral('2485')}
        </Button>
      )}
    </CustomDrawer>
  )
}
